import { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { findOperationsByCNPJs } from 'src/services/operation';
import {
  CreateOrUpdateWalletWithFunds,
  createWalletWithFunds,
  createWalletWithFundsForOwner,
} from 'src/services/wallet';
import { setBannerMessage } from 'src/store/display';
import { Operation } from 'src/types/operation';
import { formatCNPJ } from 'src/utils/document';
import { getStrapiValidationMessage } from 'src/utils/getStrapiValidationMessage';
import { truncateText } from 'src/utils/string';

import { FundSearch } from './components/FundSearch';
import { VirtualizedSelectedFundsList } from './components/VirtualizedSelectedFundsList';
import { addFundsValidationSchema } from './validation';
import { useMonitorContext } from '../../MonitorContextProvider';
import {
  isMonitorPlanLimitError,
  resolveUpgradeModalTypeFromMessage,
  UpgradeModal,
  UpgradeModalType,
} from '../../components/UpgradeModal';
import { useMonitorLimits } from '../../hooks/useMonitorLimits';
import { CSVRow } from '../../validation';
import {
  DuplicateGroup,
  DuplicateRecord,
  DuplicateRecordsModal,
} from '../DuplicateRecordsModal';

export type QuotaRecord = {
  class: string;
  series: string;
  amount: number;
};

export type Fund = {
  id: string;
  name: string;
  quotas: QuotaRecord[];
};

export type AddFundsToWalletProps = {
  availableFunds?: Operation[];
  addedFunds?: Operation[];
  onChange?: (funds: Operation[]) => void;
  onFinish?: () => void;
  onCancel?: () => void;
  onPreventClose?: (prevent: boolean) => void;
};

type OperationWithRecords = Operation & { quotas: QuotaRecord[] };

export type FormikValues = {
  name: string;
  description: string;
  startDate: Date | null;
  funds: {
    operation: number;
    quotas: {
      class: string;
      series: string;
      amount: number;
    }[];
  }[];
};

export function AddFundsToWallet({
  onChange,
  onFinish,
  onCancel,
  onPreventClose,
}: AddFundsToWalletProps) {
  const [addedFunds, setAddedFunds] = useState<OperationWithRecords[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [pendingDedup, setPendingDedup] = useState<null | CSVRow[]>(null);
  const [_, setLimitsError] = useState<string | null>(null);
  const [hasValidatedCsv, setHasValidatedCsv] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    type: UpgradeModalType;
    apiMessage: string | null;
  }>({ open: false, type: 'fund', apiMessage: null });
  const [attemptedFundsCount, setAttemptedFundsCount] = useState(0);

  const { auth } = useAuthContext();
  const { ownerId } = useParams<{ ownerId?: string }>();
  const theme = useTheme();
  const color = theme.palette.primary.main;

  // Detecta se o usuário é convidado de ambiente
  const isInvitedUser = !!ownerId;

  const {
    funds: fundsFromContext,
    handleSetFunds,
    walletDraft,
  } = useMonitorContext();

  const {
    limits,
    refetchMonitorLimits,
    validateFundsAddition,
    validateWalletCreation,
    getUniqueFundsCount,
  } = useMonitorLimits();
  // New wallet: limit applies only to funds being added to this carteira.
  const walletFundsLimitCount = getUniqueFundsCount(addedFunds);

  const openLimitUpgradeModal = (
    type: UpgradeModalType,
    attemptedCount: number,
    apiMessage: string | null = null,
  ) => {
    setAttemptedFundsCount(attemptedCount);
    setUpgradeModal({ open: true, type, apiMessage });
  };

  const handleCreateWalletError = (error: unknown) => {
    onPreventClose?.(false);
    const message = getStrapiValidationMessage(
      error as Parameters<typeof getStrapiValidationMessage>[0],
      '',
    );

    if (!isInvitedUser && isMonitorPlanLimitError(message)) {
      openLimitUpgradeModal(
        resolveUpgradeModalTypeFromMessage(message),
        1,
        message,
      );
      return;
    }

    toast.error(message || 'Não foi possível criar a carteira.');
  };

  const dispatch = useDispatch();

  const { data: apiResponse, isLoading: isLoadingFundsFromApi } = useQuery({
    queryKey: ['funds'],
    queryFn: () =>
      findOperationsByCNPJs(
        fundsFromContext?.map((f) => f.cnpj_fundo),
        ['FIDC'],
      ),
  });

  const fundsFromApi = apiResponse?.found;
  const notFoundCnpjs = apiResponse?.notFound || [];
  const nonConformingCnpjs = apiResponse?.nonConforming || [];

  const { mutate: createWalletWithFundsMutation, isLoading } = useMutation({
    mutationFn: (values: CreateOrUpdateWalletWithFunds) =>
      createWalletWithFunds(values),
    onMutate: () => {
      onPreventClose?.(true);
    },
    onSuccess: () => {
      dispatch(
        setBannerMessage({
          message: 'Carteira criada com sucesso!',
          type: 'success',
          autoclose: true,
        }),
      );
      onPreventClose?.(false);
      refetchMonitorLimits();
      onFinish?.();
    },
    onError: handleCreateWalletError,
  });

  const {
    mutate: createWalletWithFundsForOwnerMutation,
    isLoading: isLoadingCreateWalletForOwner,
  } = useMutation({
    mutationFn: (values: CreateOrUpdateWalletWithFunds) =>
      createWalletWithFundsForOwner(values, ownerId ?? ''),
    onMutate: () => {
      onPreventClose?.(true);
    },
    onSuccess: (data) => {
      const walletId = data.id;
      toast.success(
        <Box>
          <Typography variant="body1">Carteira criada com sucesso!</Typography>
          <Typography variant="body2">
            Clique&nbsp;
            <Link
              style={{ textDecoration: 'underline', color }}
              to={`/monitor/${ownerId}/wallet/${walletId}`}>
              aqui
            </Link>
            &nbsp; para acessar a carteira.
          </Typography>
        </Box>,
      );
      onPreventClose?.(false);
      onFinish?.();
    },
    onError: handleCreateWalletError,
  });

  // CNPJs não encontrados (vem diretamente da API)
  const notFoundFunds = useMemo(() => {
    return notFoundCnpjs;
  }, [notFoundCnpjs]);

  // CNPJs não conformes - não são do mercado FIDC (vem diretamente da API)
  const nonConformingFunds = useMemo(() => {
    return nonConformingCnpjs;
  }, [nonConformingCnpjs]);

  const formikInitialValues = useMemo(
    () => ({
      name: walletDraft.name,
      description: walletDraft.description,
      startDate: walletDraft.startDate,
      funds: addedFunds.map((fund) => ({
        operation: fund.id,
        quotas: fund.quotas.map((record) => ({
          class: record.class,
          series: record.series,
          amount: record.amount,
        })),
      })),
    }),
    [
      walletDraft.name,
      walletDraft.description,
      walletDraft.startDate,
      addedFunds,
    ],
  );

  const formik = useFormik<FormikValues>({
    initialValues: formikInitialValues,
    validationSchema: addFundsValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (addedFunds.length === 0) {
        dispatch(
          setBannerMessage({
            message:
              'Adicione pelo menos um fundo com cotas antes de finalizar.',
            type: 'error',
            autoclose: true,
          }),
        );
        return;
      }

      if (limits.hasLimits && !isInvitedUser) {
        const walletValidation = validateWalletCreation(
          limits.usageWallets,
          isInvitedUser,
        );
        if (!walletValidation.isValid) {
          openLimitUpgradeModal(
            'wallet',
            1,
            typeof limits.maxWallets === 'number'
              ? `Limite de carteiras excedido. Uso atual: ${limits.usageWallets} de ${limits.maxWallets}. Tentativa de adicionar: 1.`
              : null,
          );
          return;
        }

        const uniqueFundsCount = getUniqueFundsCount(addedFunds);
        const validation = validateFundsAddition(
          0,
          uniqueFundsCount,
          isInvitedUser,
        );

        if (!validation.isValid) {
          openLimitUpgradeModal(
            'fund',
            uniqueFundsCount,
            typeof limits.maxFunds === 'number'
              ? `Limite de fundos excedido. Uso atual: 0 de ${limits.maxFunds}. Tentativa de adicionar: ${uniqueFundsCount}.`
              : null,
          );
          return;
        }
      }

      if (isInvitedUser) {
        createWalletWithFundsForOwnerMutation(values);
      } else {
        createWalletWithFundsMutation(values);
      }
    },
  });

  useEffect(() => {
    return () => {
      setHasValidatedCsv(false);
    };
  }, []);

  useEffect(() => {
    if (
      fundsFromContext &&
      Array.isArray(fundsFromContext) &&
      fundsFromContext.length > 0
    ) {
      const map = new Map<string, DuplicateRecord[]>();
      fundsFromContext.forEach((row, idx) => {
        const key = `${row.cnpj_fundo}||${row.subclasse}||${row.serie}`;
        if (!map.has(key)) map.set(key, []);
        const records = map.get(key);
        if (records) {
          records.push({
            cnpj_fundo: row.cnpj_fundo,
            subclasse: row.subclasse || '',
            serie: row.serie || '',
            quantidade: row.quantidade || '',
            index: idx,
          });
        }
      });
      const dups: DuplicateGroup[] = Array.from(map.entries())
        .filter(([_, records]) => records.length > 1)
        .map(([key, records]) => ({ key, records }));
      if (dups.length > 0) {
        setDuplicateGroups(dups);
        setShowDuplicateModal(true);
        setPendingDedup(fundsFromContext);
      } else {
        setPendingDedup(null);
      }
    }
  }, [fundsFromContext]);

  const handleConfirmDuplicates = useCallback(
    (selected: { [key: string]: number }) => {
      if (!pendingDedup) return;
      const toRemove = new Set<number>();
      duplicateGroups.forEach((group) => {
        group.records.forEach((rec, idx) => {
          if (idx !== selected[group.key]) {
            toRemove.add(rec.index);
          }
        });
      });
      const filtered = pendingDedup.filter((_, idx) => !toRemove.has(idx));
      handleSetFunds(filtered);
      setShowDuplicateModal(false);
      setDuplicateGroups([]);
      setPendingDedup(null);
    },
    [pendingDedup, duplicateGroups, handleSetFunds],
  );

  const handleCancelDuplicates = useCallback(() => {
    setShowDuplicateModal(false);
    setDuplicateGroups([]);
    setPendingDedup(null);
  }, []);

  const handleKeepAllDuplicates = useCallback(() => {
    if (!pendingDedup) return;

    handleSetFunds(pendingDedup);
    setShowDuplicateModal(false);
    setDuplicateGroups([]);
    setPendingDedup(null);
  }, [pendingDedup, handleSetFunds]);

  useEffect(() => {
    if (pendingDedup) return;
    if (
      fundsFromApi &&
      Array.isArray(fundsFromApi) &&
      Array.isArray(fundsFromContext) &&
      fundsFromContext.length > 0
    ) {
      const grouped = fundsFromContext.reduce(
        (acc, curr) => {
          const key = formatCNPJ(curr.cnpj_fundo);
          if (!acc[key]) acc[key] = [];
          acc[key].push(curr);
          return acc;
        },
        {} as Record<string, typeof fundsFromContext>,
      );

      const mappedFunds: OperationWithRecords[] = Object.entries(grouped)
        .map(([cnpj, rows]) => {
          const apiFund = fundsFromApi.find(
            (f: Operation) => formatCNPJ(f.cnpj) === cnpj,
          );
          if (!apiFund) return null;
          const quotas: QuotaRecord[] = rows.map((row) => ({
            class: row.subclasse || '',
            series: row.serie || '',
            amount: row.quantidade ? Number(row.quantidade) : 0,
          }));
          return {
            ...apiFund,
            quotas,
          };
        })
        .filter(Boolean) as OperationWithRecords[];

      if (limits.hasLimits && !hasValidatedCsv && !isInvitedUser) {
        const uniqueFundsCount = getUniqueFundsCount(mappedFunds);
        const validation = validateFundsAddition(
          0,
          uniqueFundsCount,
          isInvitedUser,
        );

        if (!validation.isValid) {
          setLimitsError(validation.message || '');
          openLimitUpgradeModal(
            'fund',
            uniqueFundsCount,
            typeof limits.maxFunds === 'number'
              ? `Limite de fundos excedido. Uso atual: 0 de ${limits.maxFunds}. Tentativa de adicionar: ${uniqueFundsCount}.`
              : null,
          );
          setHasValidatedCsv(true);
          handleSetFunds([]);
          return;
        }
        setHasValidatedCsv(true);
      }

      setLimitsError(null);
      setAddedFunds(mappedFunds);
      onChange?.(mappedFunds);
    }
  }, [fundsFromApi, fundsFromContext, pendingDedup, onChange]);

  const handleAddFund = useCallback(
    (fund: Operation) => {
      if (fund && !addedFunds.find((f) => f.hashId === fund.hashId)) {
        if (limits.hasLimits && !isInvitedUser) {
          const uniqueFundsCount = getUniqueFundsCount(addedFunds);
          const validation = validateFundsAddition(
            uniqueFundsCount,
            1,
            isInvitedUser,
          );

          if (!validation.isValid) {
            setLimitsError(validation.message || '');
            openLimitUpgradeModal(
              'fund',
              1,
              typeof limits.maxFunds === 'number'
                ? `Limite de fundos excedido. Uso atual: ${uniqueFundsCount} de ${limits.maxFunds}. Tentativa de adicionar: 1.`
                : null,
            );
            return;
          }
        }

        setLimitsError(null);
        const newFund = {
          ...fund,
          quotas: [{ class: '', series: '', amount: 0 }],
        };
        setAddedFunds((prev) => {
          const newFunds = [...prev, newFund];
          onChange?.(newFunds);
          return newFunds;
        });
      }
    },
    [
      addedFunds,
      onChange,
      limits.hasLimits,
      validateFundsAddition,
      getUniqueFundsCount,
      isInvitedUser,
    ],
  );

  const handleRemoveFund = useCallback(
    (hashId: string) => {
      setAddedFunds((prev) => {
        const newFunds = prev.filter((f) => f.hashId !== hashId);
        onChange?.(newFunds);
        return newFunds;
      });
    },
    [onChange],
  );

  const handleAddRecord = useCallback((hashId: string) => {
    setAddedFunds((prev) =>
      prev.map((f) =>
        f.hashId === hashId
          ? {
              ...f,
              quotas: [
                ...f.quotas,
                {
                  class: '',
                  series: '',
                  amount: 0,
                },
              ],
            }
          : f,
      ),
    );
  }, []);

  const handleRemoveRecord = useCallback(
    (hashId: string, quota: string, series: string) => {
      setAddedFunds((prev) =>
        prev.map((f) =>
          f.hashId === hashId
            ? {
                ...f,
                quotas: f.quotas.filter(
                  (r) => !(r.class === quota && r.series === series),
                ),
              }
            : f,
        ),
      );
    },
    [],
  );

  const handleChangeRecord = useCallback(
    (
      hashId: string,
      recordIndex: number,
      field: keyof QuotaRecord,
      value: string | number,
    ) => {
      setAddedFunds((prev) =>
        prev.map((f) =>
          f.hashId === hashId
            ? {
                ...f,
                quotas: f.quotas.map((r, idx) =>
                  idx === recordIndex ? { ...r, [field]: value } : r,
                ),
              }
            : f,
        ),
      );
    },
    [],
  );

  const handleFundSelect = useCallback(
    (fund: Operation) => {
      handleAddFund(fund);
    },
    [handleAddFund],
  );

  return (
    <FormikProvider value={formik}>
      <DuplicateRecordsModal
        open={showDuplicateModal}
        duplicateGroups={duplicateGroups}
        onConfirm={handleConfirmDuplicates}
        onCancel={handleCancelDuplicates}
        onKeepAll={handleKeepAllDuplicates}
      />

      <UpgradeModal
        open={upgradeModal.open}
        onClose={() => {
          setUpgradeModal((current) => ({
            ...current,
            open: false,
            apiMessage: null,
          }));
          setLimitsError(null);
          setAttemptedFundsCount(0);
        }}
        type={upgradeModal.type}
        maxLimit={
          upgradeModal.type === 'fund' ? limits.maxFunds : limits.maxWallets
        }
        currentCount={
          upgradeModal.type === 'fund'
            ? walletFundsLimitCount
            : limits.usageWallets
        }
        attemptedCount={attemptedFundsCount}
        apiMessage={upgradeModal.apiMessage}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
        open={isLoading || isLoadingCreateWalletForOwner}>
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" color="inherit">
          Criando carteira...
        </Typography>
        <Typography variant="body2" color="inherit" textAlign="center">
          Por favor, aguarde. Não feche esta janela.
        </Typography>
      </Backdrop>

      <Box>
        <Typography variant="h4" gutterBottom>
          Adicionar cotas à Carteira - {truncateText(walletDraft.name, 30)}
        </Typography>
        <Typography variant="subtitle1">{walletDraft.description}</Typography>
        <Typography variant="subtitle1" gutterBottom>
          Selecione o fundo e informe as informações da cota que deseja
          adicionar à sua carteira.
        </Typography>
        {limits.hasLimits && !isInvitedUser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {limits.maxFunds === null
              ? 'Fundos ilimitados por carteira.'
              : `Você pode adicionar até ${limits.maxFunds} fundos por carteira.`}
            &nbsp;Atualmente usando {walletFundsLimitCount} fundos nesta
            carteira.
          </Alert>
        )}
        {isLoadingFundsFromApi && (
          <Box p={4} textAlign="center">
            <Alert severity="info">Carregando aguarde...</Alert>
          </Box>
        )}
        {!isLoadingFundsFromApi && nonConformingFunds.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Os seguintes fundos informados no CSV não são do mercado FIDC e não
            serão importados:
            <ul>
              {nonConformingFunds.map((cnpj) => (
                <li key={cnpj}>{cnpj}</li>
              ))}
            </ul>
          </Alert>
        )}
        {!isLoadingFundsFromApi && notFoundFunds.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Os seguintes fundos informados no CSV não foram encontrados:
            <ul>
              {notFoundFunds.map((cnpj) => (
                <li key={cnpj}>{cnpj}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          mb={2}
          component="form"
          onSubmit={formik.handleSubmit}>
          <FundSearch onFundSelect={handleFundSelect} />
        </Box>
        <VirtualizedSelectedFundsList
          funds={addedFunds}
          onRemoveFund={handleRemoveFund}
          onAddRecord={handleAddRecord}
          onRemoveRecord={handleRemoveRecord}
          onChangeRecord={handleChangeRecord}
        />
        <Box
          display="flex"
          gap={2}
          mt={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-around"
          width="100%"
          alignItems="center">
          <Button
            variant="outlined"
            fullWidth
            onClick={onCancel}
            disabled={isLoading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
            }}>
            <ArrowBack fontSize="small" />
            <Typography fontSize={16} fontWeight={500}>
              Voltar
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            disabled={isLoading || addedFunds.length === 0}
            onClick={() => formik.handleSubmit()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
            }}>
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <Typography fontSize={16} fontWeight={500} sx={{ ml: 1 }}>
                  Criando carteira...
                </Typography>
              </>
            ) : (
              <Typography fontSize={16} fontWeight={500}>
                Finalizar adição
              </Typography>
            )}
          </Button>
        </Box>
      </Box>
    </FormikProvider>
  );
}
